import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {FiltersBar} from "~/apps/core/ui/FiltersBar";
import {SearchAppBar, SearchAppBarProps} from "~/apps/core/ui/SearchAppBar";
import {AbsoluteContainer} from "~/apps/core/ui/AbsoluteContainer";
import {OrdersList} from "~/apps/packages/auto-shipment-orders/components/OrdersList/OrdersList";
import {Box, Button} from "@mui/material";
import {AddCircle} from "@mui/icons-material";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {useHistoryState} from "~/apps/core/utils";
import moment from "moment/moment";
import {tuple} from "~/types";
import {DateRangeChipInputProps, getOptions, DateRangeChipInput} from "./DateRangeChipInput";
import {useHistory} from "react-router-dom";
import {ToggleChipInput, ToggleChipInputProps} from "~/apps/core/ui/ChipInput/ToggleChipInput";

type FilterState = {
    archive?: boolean
    search?: string
    dateRange?: [Date, Date]
}

function useFilterState() {
    const [value, setValue, delValue] = useHistoryState<any>({
        name: "filter",
        serialize: state => JSON.stringify(state),
        deserialize: value => JSON.parse(value),
        handleBack: false,
    });

    const filter = useMemo(() => {
        const filter: FilterState = {};
        if (typeof value?.search == "string" && value.search.length > 0) {
            filter.search = value.search;
        }
        filter.archive = Boolean(value?.archive);
        if (Array.isArray(value?.dateRange) && value.dateRange.length == 2) {
            const dateRange = value.dateRange.map(x => moment(x));
            if (dateRange.filter(x => x.isValid()).length == 2) {
                filter.dateRange = dateRange.map(x => x.toDate());
            }
        }
        return filter;
    }, [value]);

    const setFilter = useCallback(async value => {
        if (value) {
            value = {...value};
            if (!value.search) {
                delete value.search;
            }
            if (!value.dateRange) {
                delete value.dateRange;
            }
            if (!value.archive) {
                delete value.archive;
            }
            if (Object.keys(value).length > 0) {
                await setValue(value);
                return;
            }
        }
        await delValue();
    }, []);

    return tuple(filter, setFilter);
}

export const IndexPage = () => {
    const $container = useRef<HTMLDivElement>();
    const [filter, setFilter] = useFilterState();
    const title = filter.search || "Наряды на авто";
    const filterDisabled = false;
    const history = useHistory();

    useEffect(() => {
        /// грязный хак для выставления дефолтного состояния фильтров. Если мы не вернулись назад то state == true
        if (!history.location.state) {
            history.replace({
                ...history.location,
                state: true,
            });
            setFilter({
                dateRange: getOptions().find(x => x.title == "Ближайшие")?.value
            }).then(() => null);
        }
    }, []);

    const handleSearchQueryChange: SearchAppBarProps["onSearch"] = (value) => {
        setFilter({
            ...filter,
            search: value,
        }).catch(console.error)
    }

    const handleDateRangeChange: DateRangeChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            dateRange: value,
        }).catch(console.error)
    }

    const handleArchiveChange: ToggleChipInputProps["onChange"] = (value) => {
        setFilter({
            ...filter,
            archive: value,
        }).catch(console.error)
    }

    const handleCreateClick = () => {
        history.push("/new");
    }

    return (
        <>
            <SearchAppBar
                position="fixed"
                color="default"
                title={title}
                disabled={filterDisabled}
                onSearch={handleSearchQueryChange}
                query={filter.search}
            >
                <FiltersBar>
                    <DateRangeChipInput
                        onChange={handleDateRangeChange}
                        value={filter.dateRange}
                        disabled={filterDisabled}
                    />
                    <ToggleChipInput label="Архив" value={filter?.archive} onChange={handleArchiveChange}/>
                </FiltersBar>
            </SearchAppBar>
            <AbsoluteContainer disableGutters top={104} ref={$container}>
                <OrdersList
                    query={filter.search}
                    dateFrom={filter?.dateRange ? filter.dateRange[0] : null}
                    dateTo={filter?.dateRange ? filter.dateRange[1] : null}
                    statuses={filter?.archive ? ["Отменен", "Завершен"] : ["В работе", "Запланирован"]}
                />
                <Box minHeight="80px"/>
            </AbsoluteContainer>
            <FloatingActions>
                <Button fullWidth color="primary" startIcon={<AddCircle/>} onClick={handleCreateClick}>
                    Создать наряд
                </Button>
            </FloatingActions>
        </>
    );
}
